/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// application
import ApplicationState from "applicationstate/ApplicationState.js";

// utils
import LoggerUtils from "utils/logger-utils/LoggerUtils.js";
import PublishSubscribeUtils from "utils/publish-subscribe-utils/PublishSubscribeUtils.js";
import ValidationUtils from "utils/validation-utils/ValidationUtils.js";

class Model {
  constructor({
    id = Model.getDefaultId(),
    lastUpdated = Model.getDefaultLastUpdated(),
    tags = Model.getDefaultTags(),
  } = {}) {

    // state
    this.state = { id, lastUpdated, tags };

    // subscriber topic
    this.topicId = PublishSubscribeUtils.createTopic({
      metadata: { id, tags },
      name: "Model-OnChange",
    });

    // setters
    this.setId(id);
    // this.setLastUpdated(lastUpdated) - called by onChange
    this.setTags(tags);
  }

  static getDefaultId() { return "new"; }
  static getDefaultLastUpdated() { return Date.now(); }
  static getDefaultTags() {
    const { applicationVersion } = ApplicationState.getState();
    return [{ version: String(applicationVersion) }];
  }

  /*====================
  getters
  ====================*/
  getId() { return String(this.state.id); }
  getLastUpdated() { return String(this.state.lastUpdated); }
  getTags() { return [ ...this.state.tags ]; }

  getModelState() {
    return {
      id: this.getId(),
      lastUpdated: this.getLastUpdated(),
      tags: this.getTags(),
    };
  }

  /*====================
  setters
  ====================*/
  setId(id) {
    if(ValidationUtils.string(id)) {
      this.state.id = String(id);
      this.onChange();
    }
    else {
      LoggerUtils.error("Unable to validate (string) id.", id);
    }
    return this.getId();
  }

  setLastUpdated(time) {
    if(ValidationUtils.string(time)) {
      this.state.lastUpdated = String(time);
    }
    else {
      LoggerUtils.error("Unable to validate (string) last update.", time);
    }
    return this.getLastUpdated();
  }

  setTags(tags) {
    if(ValidationUtils.array(tags)) {
      this.state.tags = [ ...tags ];
      this.onChange();
    }
    else {
      LoggerUtils.error("Unable to validate (array) tags.", tags);
    }
    return this.getTags();
  }

  /*====================
  onChange, publish, & subscribe
  ====================*/
  publish(message) {
    return PublishSubscribeUtils.publish(this.topicId, message);
  }

  subscribe(callback) {
    return PublishSubscribeUtils.subscribe(this.topicId, callback);
  }

  unsubscribe(subscriberId) {
    PublishSubscribeUtils.unsubscribe(this.topicId, subscriberId);
  }

  onChange() {
    this.setLastUpdated(Date.now());
    if(!this.suppressOnChange) {
      this.publish(this);
    }
  }
}
export default Model;
